// File: frontend/src/components/entries/EntryToolbar.tsx

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaSearch, FaChevronDown, FaTimes } from "react-icons/fa";
import { ShareMenu } from "../common/ShareMenu";
import { generateProfileUrl } from "../../utils";
import "./EntryToolbar.css";

export type EntrySortOption = "oldest" | "newest" | "popular";

interface EntryToolbarProps {
  sort: EntrySortOption;
  onSortChange: (sort: EntrySortOption) => void;
  filterToday: boolean;
  onFilterTodayChange: (value: boolean) => void;
  rookieOnly: boolean;
  onRookieOnlyChange: (value: boolean) => void;
  keyword: string;
  onKeywordChange: (value: string) => void;
  onScrollToBottom: () => void;
  // Username of the writer who suggested/opened this topic. Shown behind a
  // "başlığı açan" dropdown rather than inline, so a long username can never
  // stretch the toolbar row. Only custom topics ("gündem") have one, so the
  // other four domains simply omit these two props.
  suggestedByUsername?: string | null;
  suggestedByUserId?: number | null;
  // Share target of the topic page itself. When omitted the share button is
  // hidden (e.g. while no topic is selected yet).
  shareUrl?: string | null;
  shareTitle?: string | null;
}

export const EntryToolbar: React.FC<EntryToolbarProps> = ({
  sort,
  onSortChange,
  filterToday,
  onFilterTodayChange,
  rookieOnly,
  onRookieOnlyChange,
  keyword,
  onKeywordChange,
  onScrollToBottom,
  suggestedByUsername,
  suggestedByUserId,
  shareUrl,
  shareTitle,
}) => {
  const { t } = useTranslation();

  // Local input state for keyword — debounced before calling parent
  const [inputValue, setInputValue] = useState(keyword);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local input if parent keyword resets (e.g. match change)
  useEffect(() => {
    setInputValue(keyword);
  }, [keyword]);

  // Debounce keyword changes by 400ms to avoid firing a request on every keystroke
  const handleKeywordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onKeywordChange(val);
      }, 400);
    },
    [onKeywordChange]
  );

  // Clear the keyword: cancel any pending debounce and notify parent immediately
  const handleClearKeyword = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setInputValue("");
    onKeywordChange("");
  }, [onKeywordChange]);

  // Sort dropdown open state
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  // Topic author ("başlığı açan") dropdown open state — the username itself is
  // only rendered inside the panel, never in the toolbar row
  const [authorOpen, setAuthorOpen] = useState(false);
  const authorRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
      if (authorRef.current && !authorRef.current.contains(e.target as Node)) {
        setAuthorOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sortOptions: { value: EntrySortOption; labelKey: string }[] = [
    { value: "oldest", labelKey: "entry_toolbar_sort_oldest" },
    { value: "newest", labelKey: "entry_toolbar_sort_newest" },
    { value: "popular", labelKey: "entry_toolbar_sort_popular" },
  ];

  const currentSortLabel = t(
    sortOptions.find((o) => o.value === sort)?.labelKey ?? "entry_toolbar_sort_oldest"
  );

  return (
    <div className="entry-toolbar">
      {/* Keyword search input */}
      <div className="entry-toolbar__search">
        <FaSearch className="entry-toolbar__search-icon" />
        <input
          type="text"
          className="entry-toolbar__search-input"
          placeholder={t("entry_toolbar_search_placeholder")}
          value={inputValue}
          onChange={handleKeywordChange}
        />
        {inputValue && (
          <button
            className="entry-toolbar__search-clear"
            onClick={handleClearKeyword}
            type="button"
            aria-label={t("entry_toolbar_search_clear", "Temizle")}
            title={t("entry_toolbar_search_clear", "Temizle")}
          >
            <FaTimes />
          </button>
        )}
      </div>

      {/* Today filter toggle */}
      <button
        className={`entry-toolbar__filter-btn${filterToday ? " entry-toolbar__filter-btn--active" : ""}`}
        onClick={() => onFilterTodayChange(!filterToday)}
        type="button"
      >
        {t("entry_toolbar_filter_today")}
      </button>

      {/* Rookie-only filter toggle */}
      <button
        className={`entry-toolbar__filter-btn${rookieOnly ? " entry-toolbar__filter-btn--active" : ""}`}
        onClick={() => onRookieOnlyChange(!rookieOnly)}
        type="button"
      >
        {t("entry_toolbar_filter_rookies")}
      </button>

      {/* Sort dropdown */}
      <div className="entry-toolbar__sort" ref={sortRef}>
        <button
          className="entry-toolbar__sort-btn"
          onClick={() => setSortOpen((o) => !o)}
          type="button"
        >
          <span>{currentSortLabel}</span>
          <FaChevronDown className={`entry-toolbar__sort-chevron${sortOpen ? " entry-toolbar__sort-chevron--open" : ""}`} />
        </button>
        {sortOpen && (
          <div className="entry-toolbar__sort-dropdown">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                className={`entry-toolbar__sort-option${sort === opt.value ? " entry-toolbar__sort-option--active" : ""}`}
                onClick={() => {
                  onSortChange(opt.value);
                  setSortOpen(false);
                }}
                type="button"
              >
                {t(opt.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Topic author ("başlığı açan") — the username lives inside the dropdown
          panel so an arbitrarily long nick can never widen the toolbar row */}
      {suggestedByUsername && suggestedByUserId != null && (
        <div className="entry-toolbar__author" ref={authorRef}>
          <button
            type="button"
            className="entry-toolbar__author-btn"
            onClick={() => setAuthorOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={authorOpen}
          >
            {t("entry_toolbar_topic_author", "başlığı açan")}
            <FaChevronDown
              className={`entry-toolbar__sort-chevron ${authorOpen ? "entry-toolbar__sort-chevron--open" : ""}`}
            />
          </button>

          {authorOpen && (
            <div className="entry-toolbar__author-dropdown" role="menu">
              <Link
                to={generateProfileUrl(suggestedByUsername, suggestedByUserId)}
                className="entry-toolbar__author-link"
                title={`@${suggestedByUsername}`}
                onClick={() => setAuthorOpen(false)}
              >
                @{suggestedByUsername}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Share this topic */}
      {shareUrl && (
        <ShareMenu url={shareUrl} title={shareTitle ?? ""} variant="toolbar" />
      )}

      {/* Scroll to bottom button */}
      <button
        className="entry-toolbar__bottom-btn"
        onClick={onScrollToBottom}
        type="button"
        title={t("entry_toolbar_scroll_bottom")}
      >
        {t("entry_toolbar_scroll_bottom")}
      </button>
    </div>
  );
};